import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Search, 
  Filter, 
  ChevronRight,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Trash2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Progress } from "../../components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import DashboardLayout from "../../components/DashboardLayout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCases = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [riskFilter, setRiskFilter] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const res = await axios.get(`${API}/admin/cases`);
      setCases(res.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCase = async () => {
    if (!caseToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/cases/${caseToDelete.id}`);
      toast.success("Caso eliminado correctamente");
      // Refetch data to ensure consistency (avoid UI freeze from state mutation)
      await fetchCases();
      // Force close dialog first, then clear state
      setDeleteDialogOpen(false);
      setTimeout(() => {
        setCaseToDelete(null);
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar el caso");
      setDeleteDialogOpen(false);
      setCaseToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteDialog = (caseItem) => {
    setCaseToDelete(caseItem);
    setDeleteDialogOpen(true);
  };

  const filteredCases = cases.filter(c => {
    const matchesSearch = c.user?.name?.toLowerCase().includes(search.toLowerCase()) ||
                         c.user?.email?.toLowerCase().includes(search.toLowerCase());
    const matchesRisk = riskFilter === "all" || c.risk_level === riskFilter;
    return matchesSearch && matchesRisk;
  });

  const getRiskBadge = (level) => {
    switch (level) {
      case "low":
        return <span className="badge-success">Bajo</span>;
      case "medium":
        return <span className="badge-warning">Medio</span>;
      case "high":
        return <span className="badge-danger">Alto</span>;
      default:
        return <span className="badge-info">Pendiente</span>;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gold-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
        >
          <div>
            <h1 className="font-heading text-3xl font-bold text-navy-900 mb-2">
              Todos los Casos
            </h1>
            <p className="text-navy-600">{cases.length} casos en total</p>
          </div>
          <Link to="/admin">
            <Button variant="outline">Volver al Dashboard</Button>
          </Link>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-elevated p-4 flex flex-col sm:flex-row gap-4"
        >
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
            <Input
              placeholder="Buscar por nombre o email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-cases-input"
            />
          </div>
          <Select value={riskFilter} onValueChange={setRiskFilter}>
            <SelectTrigger className="w-full sm:w-48" data-testid="risk-filter-select">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrar por riesgo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="low">Bajo riesgo</SelectItem>
              <SelectItem value="medium">Riesgo medio</SelectItem>
              <SelectItem value="high">Alto riesgo</SelectItem>
              <SelectItem value="pending">Pendiente</SelectItem>
            </SelectContent>
          </Select>
        </motion.div>

        {/* Cases Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-elevated overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-navy-50 border-b border-navy-200">
                <tr>
                  <th className="text-left py-4 px-6 font-semibold text-navy-700">Cliente</th>
                  <th className="text-left py-4 px-6 font-semibold text-navy-700">Visa</th>
                  <th className="text-left py-4 px-6 font-semibold text-navy-700">Riesgo</th>
                  <th className="text-left py-4 px-6 font-semibold text-navy-700">Progreso</th>
                  <th className="text-left py-4 px-6 font-semibold text-navy-700">Paso</th>
                  <th className="text-left py-4 px-6 font-semibold text-navy-700">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-100">
                {filteredCases.map((caseItem) => (
                  <tr key={caseItem.id} className="hover:bg-navy-50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gold-100 rounded-full flex items-center justify-center">
                          <span className="font-semibold text-gold-700">
                            {caseItem.user?.name?.[0] || "?"}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-navy-900">{caseItem.user?.name}</p>
                          <p className="text-sm text-navy-500">{caseItem.user?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="font-medium text-navy-900">{caseItem.visa_type}</span>
                    </td>
                    <td className="py-4 px-6">
                      {getRiskBadge(caseItem.risk_level)}
                    </td>
                    <td className="py-4 px-6">
                      <div className="w-32">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-navy-500">{caseItem.progress_percentage}%</span>
                        </div>
                        <Progress value={caseItem.progress_percentage} className="h-2" />
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-navy-700">Paso {caseItem.current_step}/6</span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <Link to={`/admin/cases/${caseItem.id}`}>
                          <Button size="sm" variant="outline" data-testid={`view-case-${caseItem.id}-btn`}>
                            Ver detalles
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => openDeleteDialog(caseItem)}
                          data-testid={`delete-case-${caseItem.id}-btn`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredCases.length === 0 && (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-navy-300 mx-auto mb-4" />
              <p className="text-navy-500">No se encontraron casos</p>
            </div>
          )}
        </motion.div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Eliminar Caso
              </AlertDialogTitle>
              <AlertDialogDescription>
                ¿Estás seguro de que deseas eliminar el caso de <strong>{caseToDelete?.user?.name}</strong>?
                <br /><br />
                Esta acción eliminará:
                <ul className="list-disc list-inside mt-2 text-sm">
                  <li>El caso y su progreso</li>
                  <li>Las notas del caso</li>
                  <li>Las solicitudes de documentos</li>
                </ul>
                <br />
                <span className="text-red-600 font-medium">Esta acción no se puede deshacer.</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting} onClick={() => setCaseToDelete(null)}>
                Cancelar
              </AlertDialogCancel>
              <Button
                onClick={handleDeleteCase}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar Caso
                  </>
                )}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminCases;
